if (!customElements.get('shoppable-image')) {
  class ShoppableImage extends HTMLElement {
    // Index of the product shown by default on desktop (updated on click), and
    // of the card the open fold-out shows on mobile.
    #committedIndex = 0;
    // Index of the product temporarily previewed on hover, or null when no
    // hotspot is hovered (then the committed product is shown).
    #previewIndex = null;

    connectedCallback() {
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      // Blank-product blocks are skipped on the storefront, so the first
      // rendered card is not necessarily data-index="0". The section tells us
      // which one it rendered visible; without this the panel would start with
      // every card hidden whenever block 0 has no product.
      const initial = parseInt(this.dataset.initialIndex ?? '0', 10);
      if (!Number.isNaN(initial)) this.#committedIndex = initial;

      // Below 1024px the side panel is hidden and every dot opens the fold-out
      // card carousel under the image on its own product instead (ClickUp
      // 869fjtb4q). Tracked live so a rotation or resize across the breakpoint
      // re-renders the active state for the layout now showing.
      this._mobileCards = this.querySelector('.shoppable-image__mobile-cards');
      this._carousel = this._mobileCards?.querySelector('ilva-carousel');
      this._mobileQuery = window.matchMedia('(max-width: 1023.98px)');
      this._mobileQuery.addEventListener('change', this._onLayoutChange.bind(this), { signal });

      this.addEventListener('click', this._onClick.bind(this), { signal });

      // Swiping the cards moves the selected dot with them. scrollend fires
      // once the scroll settles; without it, a debounced scroll keeps a smooth
      // scroll across several cards from stepping the dot through each one.
      const slider = this._mobileCards?.querySelector('.shoppable-image__mobile-slider');
      if (slider) {
        if ('onscrollend' in window) {
          slider.addEventListener('scrollend', this._onCardsScrolled.bind(this), { signal });
        } else {
          let scrollTimer;
          slider.addEventListener('scroll', () => {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => this._onCardsScrolled(), 150);
          }, { signal });
        }
      }

      // Desktop hover preview: hovering a hotspot previews its product; the
      // committed product returns once the pointer leaves the image. Gated on a
      // real hover-capable pointer at desktop width so it never fires on touch
      // (where a tap opens the dot's product in the fold-out instead).
      if (window.matchMedia('(hover: hover) and (min-width: 1024px)').matches) {
        this.addEventListener('mouseover', this._onHover.bind(this), { signal });
        const image = this.querySelector('.shoppable-image__image');
        image?.addEventListener('mouseleave', this._onLeave.bind(this), { signal });
      }

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (evt) => {
          const index = parseInt(evt.target.dataset.index ?? '0', 10);
          this.#committedIndex = index;
          this.#previewIndex = null;
          // On mobile the card lives in the fold-out — open it on that card so
          // selecting a block in the editor shows its product.
          if (this._mobileQuery.matches) this._openCards(index);
          this._render();
        }, { signal });
      }

      this._render();
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    _onClick(evt) {
      const btn = evt.target.closest('.hotspot__btn');
      if (!btn) return;
      const index = parseInt(btn.closest('.hotspot')?.dataset.index ?? '0', 10);
      this.#previewIndex = null;

      // Mobile: a dot opens the fold-out on its own product; tapping the dot
      // that is already selected closes it again.
      if (this._mobileQuery.matches && this._mobileCards) {
        if (this._cardsOpen() && index === this.#committedIndex) {
          this._mobileCards.classList.remove('is-open');
        } else {
          this.#committedIndex = index;
          this._openCards(index);
        }
        this._render();
        return;
      }

      // Desktop: clicking a hotspot commits its product as the new default.
      this.#committedIndex = index;
      this._render();
    }

    _onHover(evt) {
      const hotspot = evt.target.closest('.hotspot');
      if (!hotspot) return;
      const index = parseInt(hotspot.dataset.index ?? '0', 10);
      if (index === this.#previewIndex) return;
      this.#previewIndex = index;
      this._render();
    }

    _onLeave() {
      if (this.#previewIndex === null) return;
      this.#previewIndex = null;
      this._render();
    }

    _onLayoutChange() {
      // The fold-out is mobile-only, so leaving mobile closes it. Coming back,
      // no dot is selected until the next tap — the same state as on load.
      if (!this._mobileQuery.matches) this._mobileCards?.classList.remove('is-open');
      this._render();
    }

    _cardsOpen() {
      return Boolean(this._mobileCards?.classList.contains('is-open'));
    }

    _renderedCards() {
      return [...(this._mobileCards?.querySelectorAll('.shoppable-mobile-card') ?? [])];
    }

    _openCards(index) {
      if (!this._mobileCards) return;
      const wasOpen = this._cardsOpen();
      this._mobileCards.classList.add('is-open');
      // Blank-product blocks are skipped on the storefront, so a card's page in
      // the carousel is its position among the rendered cards, not data-index.
      const page = this._renderedCards().findIndex((card) => parseInt(card.dataset.index, 10) === index);
      const carousel = this._carousel;
      if (page < 0 || typeof carousel?.scrollToPage !== 'function') return;
      // Opening from closed: jump straight to the card, so the cards don't slide
      // past while the fold-out is still growing. Already open: slide to it.
      // 'instant' overrides the slider's CSS scroll-behavior: smooth; a browser
      // that rejects the value falls back to the carousel's own smooth scroll.
      if (!wasOpen && carousel.slider && typeof carousel.pageOffset === 'function') {
        try {
          carousel.slider.scrollTo({ left: (carousel.dirSign || 1) * carousel.pageOffset(page), behavior: 'instant' });
          return;
        } catch (err) {
          // fall through
        }
      }
      carousel.scrollToPage(page);
    }

    _onCardsScrolled() {
      if (!this._mobileQuery.matches || !this._cardsOpen()) return;
      if (typeof this._carousel?.currentPage !== 'function') return;
      const card = this._renderedCards()[this._carousel.currentPage()];
      const index = parseInt(card?.dataset.index ?? '', 10);
      if (Number.isNaN(index) || index === this.#committedIndex) return;
      this.#committedIndex = index;
      this._render();
    }

    _render() {
      // Hover preview wins over the committed selection; fall back to committed.
      const shown = this.#previewIndex ?? this.#committedIndex;
      // On mobile a dot reads as selected only while its card shows in the open
      // fold-out — before the first tap, and after closing, every dot pulses
      // alike. Desktop always shows the committed product in the side panel.
      const showActive = !this._mobileQuery.matches || this._cardsOpen();

      // Active styling (and aria-pressed) tracks the committed selection so it
      // doesn't flicker on hover — hover gets its own :hover style in CSS. The
      // previewed card is the one made visible in the panel.
      this.querySelectorAll('.hotspot').forEach((el) => {
        const i = parseInt(el.dataset.index, 10);
        const committed = showActive && i === this.#committedIndex;
        el.classList.toggle('is-active', committed);
        el.querySelector('.hotspot__btn')?.setAttribute('aria-pressed', String(committed));
      });

      this.querySelectorAll('.shoppable-product-card').forEach((el) => {
        el.classList.toggle('is-hidden', parseInt(el.dataset.index, 10) !== shown);
      });
    }
  }

  customElements.define('shoppable-image', ShoppableImage);
}
